import { useMemo, useState } from "react";
import * as yup from "yup";
import { I18n } from "react-redux-i18n";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { Auth } from "aws-amplify";
import { normalizeUser } from "../../utils/normalizeUser";
import {
  handleNewsletterSubscriptions,
  setUserProfile,
} from "../../state/actions/user";
import { connect } from "react-redux";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from "@material-ui/core";
import FormButton from "./FormButton";
import {
  trackNewSurveyUser,
  trackNewsletterSubscription,
} from "../../utils/analytics";
import { AUTHENTICATOR_WELCOME } from "./constants";

const VerifySignup = ({
  initialValue,
  setUserProfile,
  setAuthScreen,
  handleNewsletterSubscriptions,
}) => {
  const [loader, setLoader] = useState(false);
  const [resendLoader, setResendLoader] = useState(false);
  const [error, setError] = useState(false);

  const schema = useMemo(() => {
    return yup.object({
      code: yup.string().required(I18n.t("authenticator.required")),
    });
  }, []);

  const {
    handleSubmit,
    control,
    formState: { errors },
    formState: { isSubmitted },
  } = useForm({
    defaultValues: {
      code: "",
    },
    resolver: yupResolver(schema),
  });

  const codeErrorMsg = useMemo(() => {
    if (error) return I18n.t("authenticator.CodeMismatchException");

    return errors?.code?.message || "";
  }, [errors?.code?.message, error]);

  const onResend = async () => {
    setResendLoader(true);

    try {
      await Auth.resendSignUp(initialValue.email);
    } catch (e) {
      console.log(e);
    }

    setResendLoader(false);
  };

  const onSubmit = async (data) => {
    setError(false);
    setLoader(true);

    try {
      await Auth.confirmSignUp(initialValue.email, data.code);

      try {
        const user = await Auth.signIn(
          initialValue.email,
          initialValue.password
        );

        const normalizedUser = normalizeUser(user);

        await handleNewsletterSubscriptions(
          initialValue.newsletters,
          normalizedUser
        );
        trackNewsletterSubscription(initialValue.newsletters);
        trackNewSurveyUser();

        return setUserProfile(normalizedUser);
      } catch (e) {
        console.log(e);

        return setAuthScreen(AUTHENTICATOR_WELCOME);
      }
    } catch (e) {
      console.log(e);

      if (e.code === "CodeMismatchException") setError(true);
    }

    setLoader(false);
  };

  return (
    <Grid
      container
      spacing={2}
      component="form"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Grid item xs={12} style={{ display: "flex", flexDirection: "column" }}>
        <Typography
          variant="h4"
          color="primary"
          style={{
            fontFamily: "'Paytone One', sans- serif",
            marginBottom: "4px",
          }}
        >
          {I18n.t("authenticator.confirmSignUpTitle")}
        </Typography>
      </Grid>

      <Grid item xs={12} style={{ display: "flex", flexDirection: "column" }}>
        <Controller
          name="code"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TextField
              label={I18n.t("authenticator.enterVerificationCode")}
              variant="outlined"
              {...field}
              error={(isSubmitted && Boolean(errors.code)) || error}
              helperText={codeErrorMsg}
            />
          )}
        />
      </Grid>

      <Box style={{ alignSelf: "center", margin: "20px auto" }}>
        <FormButton
          {...{
            label: I18n.t("authenticator.verify"),
            loader,
            disabled: resendLoader,
          }}
        />
      </Box>

      <Grid
        item
        xs={12}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography style={{ marginRight: "4px" }} color="textSecondary">
          {I18n.t("authenticator.notThereYet")}
        </Typography>

        {resendLoader ? (
          <CircularProgress size="20px" style={{ marginLeft: "4px" }} />
        ) : (
          <Button color="primary" disabled={loader} onClick={onResend}>
            {I18n.t("authenticator.resendCode")}
          </Button>
        )}
      </Grid>
    </Grid>
  );
};

const mapDispatchToProps = (dispatch) => ({
  setUserProfile: (user) => dispatch(setUserProfile(user)),
  handleNewsletterSubscriptions: (newsletters, user) =>
    dispatch(handleNewsletterSubscriptions(newsletters, user)),
});

export default connect(null, mapDispatchToProps)(VerifySignup);
